import { Trans, useLingui } from '@lingui/react/macro'

import {
  Alert,
  Button,
  List,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { IconInfoCircle, IconSparkles } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { AttachmentField } from '$src/components/common/AttachmentField'
import { useAttachments } from '$src/lib/hooks/useAttachments'
import { SUBSCRIPTION_SALES_ENABLED } from '$src/config/features'
import { propertyHooks } from '$src/lib/trpcHooks'
import { SystemTaskFormData } from './types'

interface SystemTaskFormProps {
  data: SystemTaskFormData
  onChange: (data: SystemTaskFormData) => void
  markAsCompleted: boolean
  onMarkAsCompletedChange: (value: boolean) => void
  disabled?: boolean
  isMarkkuTask?: boolean
  hasPackage: boolean
  attachments: ReturnType<typeof useAttachments>
}

export function SystemTaskForm({
  data,
  onChange,
  markAsCompleted,
  onMarkAsCompletedChange,
  disabled,
  isMarkkuTask = false,
  hasPackage,
  attachments,
}: SystemTaskFormProps) {
  const { t } = useLingui()
  const navigate = useNavigate()
  const { data: propertiesResponse } = propertyHooks.useList()

  const handleFieldChange = (field: keyof SystemTaskFormData, value: any) => {
    onChange({ ...data, [field]: value })
  }

  // Without a package there is nothing to upsell any more, so the resident gets the
  // normal form and handles the task themselves.
  if (isMarkkuTask && (hasPackage || SUBSCRIPTION_SALES_ENABLED)) {
    // Show upsell if no package
    if (!hasPackage) {
      const firstProperty = propertiesResponse?.data[0]

      return (
        <Stack gap="md">
          <Alert
            icon={<IconSparkles size={20} />}
            title={t`Part of the Markku package`}
            color="orange"
          >
            <Stack gap="md">
              <Text>
                <Trans>
                  This maintenance task is part of the Markku package. When you
                  get the package, professionals will handle this task for you.
                </Trans>
              </Text>

              <List
                spacing="xs"
                size="sm"
                icon={
                  <IconSparkles
                    size={16}
                    color="var(--mantine-color-brandOrange-6)"
                  />
                }
              >
                <List.Item>
                  <Trans>Regular maintenance - no surprises</Trans>
                </List.Item>
                <List.Item>
                  <Trans>Professionals handle the tasks</Trans>
                </List.Item>
                <List.Item>
                  <Trans>Peace of mind and time savings</Trans>
                </List.Item>
                <List.Item>
                  <Trans>Your property value is preserved</Trans>
                </List.Item>
              </List>

              <Button
                color="brandOrange"
                onClick={() => {
                  if (firstProperty) {
                    navigate({
                      to: '/properties/$propertyId',
                      params: { propertyId: String(firstProperty.id) },
                      search: { tab: 'package' },
                    })
                  }
                }}
              >
                <Trans>Explore Markku packages</Trans>
              </Button>
            </Stack>
          </Alert>
        </Stack>
      )
    }

    // Show regular info if has package
    return (
      <Stack gap="md">
        <Alert
          icon={<IconInfoCircle size={20} />}
          title={t`Markku handles this task`}
          color="blue"
        >
          <Stack gap="xs">
            <Text>
              <Trans>
                This maintenance task is part of your Markku service agreement.
                Markku's professionals will handle this task for you according
                to the agreed schedule.
              </Trans>
            </Text>
            <Text>
              <Trans>
                If you have questions about this task or notice a problem,
                contact our customer service.
              </Trans>
            </Text>
          </Stack>
        </Alert>
      </Stack>
    )
  }

  // For user tasks, show the regular form
  return (
    <Stack gap="md">
      <Switch
        label={<Trans>Mark as completed</Trans>}
        checked={markAsCompleted}
        onChange={(event) =>
          onMarkAsCompletedChange(event.currentTarget.checked)
        }
        disabled={disabled}
        size="md"
      />

      {markAsCompleted && (
        <DateInput
          label={<Trans>Work completion date</Trans>}
          placeholder={t`Select date`}
          value={data.completedDate ? new Date(data.completedDate) : new Date()}
          onChange={(value) => {
            handleFieldChange('completedDate', value || '')
          }}
          required
          disabled={disabled}
        />
      )}

      <Textarea
        label={<Trans>Notes about the task (optional)</Trans>}
        placeholder={t`Add notes about the task...`}
        value={data.notes}
        onChange={(e) => handleFieldChange('notes', e.currentTarget.value)}
        minRows={3}
        disabled={disabled}
      />

      <AttachmentField
        attachments={attachments.attachments}
        isUploading={attachments.isUploading}
        uploadFile={attachments.uploadFile}
        removeAttachment={attachments.removeAttachment}
        disabled={disabled}
      />
    </Stack>
  )
}
